import { createSlice } from '@reduxjs/toolkit'

import {
  FRAUD_CHECK_COMPLETED,
  getDataFromSessionStorage,
  removeDataFromSessionStorage,
  setDataToSessionStorage,
} from 'src/utils/sessionStorage'

import type { UserState } from './types'
import type {
  ILevel,
  LiveChatSettings,
  PaymentMetaDataInfo,
  User,
} from '@patrianna/shared-patrianna-types/store/UserModule'
import type { PayloadAction } from '@reduxjs/toolkit'
import type { ITabsConfig } from 'src/containers/Layouts/GamesLayout/TabNavigation'

const initialState: UserState = {
  info: null,
  location: {
    country: null,
  },
  xpLevel: null,
  vipLevel: null,
  firstOffer: null,
  userFlowIsRunning: false,
  isUpdateingLegals: false,
  paymentMetaDataInfo: {
    firstDeposit: null,
    lastDeposit: null,
    firstWithdraw: null,
  },
  liveChatSettings: {
    showLiveChat: false,
    purchaseFlowEnabled: false,
    email: null,
    external_id: null,
  },
  socialAuthData: null,
  userPasswordChanged: false,
  tabsConfig: {
    categories: [],
    shouldBeRedirected: false,
  },
  lastActivity: null,
  loyaltyInactivityShown: false,
  fraudCheckCompleted: getDataFromSessionStorage(FRAUD_CHECK_COMPLETED),
  geoVerificationRequired: null,
}

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<{ data: User }>) => {
      state.info = action.payload.data
    },

    setUserCountry: (state, action: PayloadAction<{ country: string }>) => {
      state.location.country = action.payload.country
    },

    setXPLevel: (state, action: PayloadAction<{ xpLevel: ILevel }>) => {
      state.xpLevel = action.payload.xpLevel
    },

    setVipLevel: (state, action: PayloadAction<{ vipLevel: ILevel }>) => {
      state.vipLevel = action.payload.vipLevel
    },

    setIsEmailVerified: (state, action: PayloadAction<{ isVerified: boolean }>) => {
      state.info.emailVerified = action.payload.isVerified
    },

    deleteUser: () => {
      removeDataFromSessionStorage(FRAUD_CHECK_COMPLETED)

      return initialState
    },

    setUserFlowIsRunning: (state, action: PayloadAction<{ userFlowIsRunning: boolean }>) => {
      state.userFlowIsRunning = action.payload.userFlowIsRunning
    },

    setUserVipSubscription: (state, action: PayloadAction<{ subscription: string }>) => {
      const { subscriptions } = state.info
      state.info.subscriptions = subscriptions
        ? subscriptions.concat({ code: action.payload.subscription, offerCode: action.payload.subscription })
        : [].concat({ code: action.payload.subscription })
    },

    setSentEmail: (state) => {
      state.info.sentWelcomeEmail = true
    },

    setIsUpdateLegals: (state, action: PayloadAction<{ isUpdateingLegals: boolean }>) => {
      state.isUpdateingLegals = action.payload.isUpdateingLegals
    },

    setFirstDepositDate: (state, action: PayloadAction<{ firstDepositDate: string }>) => {
      state.paymentMetaDataInfo.firstDeposit = action.payload.firstDepositDate
    },

    setPaymentMetaDataInfo: (state, action: PayloadAction<PaymentMetaDataInfo>) => {
      state.paymentMetaDataInfo = {
        firstDeposit: action.payload.firstDeposit || null,
        lastDeposit: action.payload.lastDeposit || null,
        firstWithdraw: action.payload.firstWithdraw || null,
      }
    },

    setLiveChatSettings: (state, action: PayloadAction<{ liveChatSettings: LiveChatSettings }>) => {
      state.liveChatSettings = action.payload.liveChatSettings
    },

    setSocialAuthData: (state, action: PayloadAction<UserState['socialAuthData']>) => {
      state.socialAuthData = action.payload
    },

    removeSocialAuthData: (state) => {
      state.socialAuthData = null
    },

    setUserPasswordChanged: (state) => {
      state.userPasswordChanged = true
    },

    setTabsConfig: (state, action: PayloadAction<{ tabsConfig: ITabsConfig }>) => {
      state.tabsConfig = action.payload.tabsConfig
    },
    setLocationStatus: (state, action: PayloadAction<{ locationStatus: User['locationStatus'] }>) => {
      if (state?.info?.locationStatus !== action.payload.locationStatus) {
        state.info.locationStatus = action.payload.locationStatus
      }
    },
    setSignUpFraudInfo: (
      state,
      action: PayloadAction<{ fraudCheckCompleted: boolean; geoVerificationRequired: boolean }>,
    ) => {
      state.fraudCheckCompleted = action.payload.fraudCheckCompleted
      state.geoVerificationRequired = action.payload.geoVerificationRequired

      setDataToSessionStorage(FRAUD_CHECK_COMPLETED, action.payload.fraudCheckCompleted)
    },
    setAccountMode: (state, action: PayloadAction<{ mode: User['mode'] }>) => {
      if (state.info) {
        state.info.mode = action.payload.mode
      }
    },
    setLastActivity: (state, action: PayloadAction<{ lastActivity: string }>) => {
      state.lastActivity = action.payload.lastActivity
    },
    setLoyaltyInactivityShown: (state) => {
      state.loyaltyInactivityShown = true
    },
  },
})

export type ActionsTypes = ReturnType<InferValueTypes<typeof userSlice.actions>>
export const { actions } = userSlice
